import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import {
  Loader2, Building2, Calendar, FileText, MapPin, IndianRupee,
  Clock, AlertTriangle, TrendingUp, TrendingDown, Hash, ShieldCheck,
} from "lucide-react";

const fmtINR = (v) => v == null || isNaN(v) ? "—" : "₹" + Math.round(v).toLocaleString("en-IN");
const fmtL = (v) => v == null || isNaN(v) ? "—" : "₹" + (Number(v) / 100000).toFixed(2) + "L";
const fmtPct = (v) => v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`;
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
};

const lifecycleClasses = (tone) => ({
  indigo:  "text-indigo-800 bg-indigo-100 border-indigo-300",
  amber:   "text-amber-800 bg-amber-100 border-amber-300",
  rose:    "text-rose-800 bg-rose-100 border-rose-300",
  emerald: "text-emerald-800 bg-emerald-100 border-emerald-300",
}[tone] || "text-zinc-700 bg-zinc-100 border-zinc-200");

const varianceTone = (pct) => {
  if (pct == null) return "text-zinc-500 bg-zinc-100";
  if (pct <= 0) return "text-emerald-700 bg-emerald-50";
  if (pct <= 5) return "text-amber-700 bg-amber-50";
  return "text-rose-700 bg-rose-50";
};

/** Rent property dialog — lease agreement details (from Rent Master) + 12-month rent history. */
export default function PropertyRentDialog({ open, onClose, target }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fy, setFy] = useState("recent"); // "recent" = last 12 mo; else "FY 24-25" etc.

  useEffect(() => {
    if (!open || !target) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    const qp = new URLSearchParams();
    if ((target.locations || []).length > 1) {
      qp.set("locations", target.locations.join(","));
    } else {
      qp.set("location", target.locations?.[0] || target.name);
    }
    if (fy && fy !== "recent") qp.set("fy", fy);
    qp.set("months", "12");
    api.get(`/mis-analysis/rent-detail?${qp.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { logError("rent-detail failed", e); if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, target, fy]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="property-rent-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-display">
            <Building2 className="w-5 h-5 text-zinc-700" />
            Rent · {target?.name || target?.locations?.[0]}
            {target?.locations?.length > 1 && (
              <span className="text-xs font-normal text-zinc-500">({target.locations.length} properties)</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading rent detail…
          </div>
        )}
        {!loading && !data && (
          <div className="py-12 text-center text-zinc-400 text-sm">No rent data available for this property.</div>
        )}

        {!loading && data && (
          <div className="space-y-5">
            {/* Period summary strip */}
            <SummaryStrip summary={data.summary} fy={fy} setFy={setFy} availableFys={data.available_fys} />

            {/* Lease agreement card(s) */}
            <div className={`grid gap-3 ${data.agreements.length > 1 ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
              {data.agreements.map((agr) => (
                <AgreementCard key={agr.location} agreement={agr} lifecycleClasses={lifecycleClasses} />
              ))}
            </div>

            {/* Monthly history table */}
            <RentHistoryTable rows={data.rows} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryStrip({ summary, fy, setFy, availableFys }) {
  if (!summary) return null;
  return (
    <div className="bg-gradient-to-br from-zinc-50 to-white border-2 border-zinc-200 rounded-xl p-4">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex items-center gap-6 flex-wrap">
          <Stat label="Total rent" value={fmtL(summary.rent_total)} note={`${summary.months_count} months`} />
          <Stat
            label="Avg monthly"
            value={fmtL(summary.avg_monthly_rent)}
          />
          {summary.aop_overlap_months > 0 && (
            <Stat
              label="Variance vs AOP"
              value={summary.variance_pct == null ? "—" : `${summary.variance_pct > 0 ? "+" : ""}${summary.variance_pct}%`}
              note={`${summary.aop_overlap_months}/${summary.months_count} mo overlap`}
              tone={varianceTone(summary.variance_pct)}
            />
          )}
        </div>
        <FyPicker fy={fy} setFy={setFy} availableFys={availableFys} />
      </div>
    </div>
  );
}

function Stat({ label, value, note, tone }) {
  return (
    <div className={`px-3 py-2 rounded-lg ${tone || ""}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
      <div className="font-display font-black text-xl tabular-nums">{value}</div>
      {note && <div className="text-[10px] text-zinc-500 font-medium">{note}</div>}
    </div>
  );
}

function FyPicker({ fy, setFy, availableFys }) {
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="rent-dialog-fy-picker">
      <button
        onClick={() => setFy("recent")}
        className={`text-[10px] px-2 py-1 rounded border ${fy === "recent" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"}`}
      >
        Last 12 mo
      </button>
      {(availableFys || []).map((y) => (
        <button
          key={y}
          onClick={() => setFy(y)}
          className={`text-[10px] px-2 py-1 rounded border ${fy === y ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"}`}
        >
          {y}
        </button>
      ))}
    </div>
  );
}

function AgreementCard({ agreement, lifecycleClasses }) {
  const ag = agreement.agreement;
  return (
    <div className="border-2 border-zinc-200 rounded-xl p-4 bg-white" data-testid={`rent-agreement-${agreement.location}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-zinc-500" />
            <span className="font-bold text-zinc-900">{agreement.location}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${lifecycleClasses(agreement.lifecycle_tone)}`}>
              {agreement.lifecycle_label}
            </span>
          </div>
          {ag?.party_name && (
            <div className="text-sm text-zinc-700">
              <span className="text-zinc-500">Landlord:</span> <span className="font-medium">{ag.party_name}</span>
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Latest rent</div>
          <div className="font-display font-black text-lg tabular-nums">{fmtL(agreement.latest_rent)}</div>
          {agreement.latest_variance_pct != null && (
            <div className={`text-[10px] font-bold ${agreement.latest_variance_pct > 0 ? "text-rose-600" : "text-emerald-600"}`}>
              {agreement.latest_variance_pct > 0 ? "+" : ""}{agreement.latest_variance_pct}% vs plan
            </div>
          )}
        </div>
      </div>

      {!ag && (
        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs">
          <AlertTriangle className="w-4 h-4" />
          No lease agreement found in Rent Master for this property.
        </div>
      )}

      {ag && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          {ag.address && <Field icon={<MapPin className="w-3 h-3" />} label="Address" value={ag.address} colSpan={2} />}
          {ag.area_sqft != null && <Field icon={<Hash className="w-3 h-3" />} label="Area" value={`${ag.area_sqft.toLocaleString("en-IN")} sq.ft`} />}
          {ag.gst_no && <Field label="GST" value={ag.gst_no} />}
          {ag.lease_sign_date && <Field icon={<Calendar className="w-3 h-3" />} label="Sign date" value={fmtDate(ag.lease_sign_date)} />}
          {ag.lease_start_date && <Field icon={<Calendar className="w-3 h-3" />} label="Lease start" value={fmtDate(ag.lease_start_date)} />}
          {ag.end_date && <Field icon={<Calendar className="w-3 h-3" />} label="Lease end" value={fmtDate(ag.end_date)} />}
          {ag.lease_tenure && <Field icon={<Clock className="w-3 h-3" />} label="Tenure" value={ag.lease_tenure} />}
          {ag.lock_in_period && <Field icon={<ShieldCheck className="w-3 h-3" />} label="Lock-in" value={ag.lock_in_period} />}
          {ag.lock_in_end_date && <Field label="Lock-in ends" value={fmtDate(ag.lock_in_end_date)} />}
          {ag.notice_period && <Field label="Notice period" value={ag.notice_period} />}
          {ag.rent_free_period && <Field label="Rent-free" value={ag.rent_free_period} />}
          {ag.rent_start_date && <Field label="Rent starts" value={fmtDate(ag.rent_start_date)} />}
          {ag.escalation && <Field label="Escalation" value={ag.escalation} colSpan={2} />}
          {ag.payment_terms && <Field label="Payment terms" value={ag.payment_terms} />}
          {ag.rcm_applicable && <Field label="RCM" value={ag.rcm_applicable} />}
          {ag.security_deposit != null && (
            <Field icon={<IndianRupee className="w-3 h-3" />} label="Security deposit" value={fmtINR(ag.security_deposit)} />
          )}
        </div>
      )}
    </div>
  );
}

function Field({ icon, label, value, colSpan }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="font-medium text-zinc-800 break-words">{value}</div>
    </div>
  );
}

function RentHistoryTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="text-center text-xs text-zinc-400 py-6">No monthly rent history available.</div>;
  }
  return (
    <div className="border-2 border-zinc-200 rounded-xl overflow-hidden">
      <div className="px-4 py-2 bg-zinc-50 border-b-2 border-zinc-200 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-700">Monthly history</span>
        <span className="text-[10px] text-zinc-500">{rows.length} months · newest first</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
            <tr>
              <th className="px-3 py-2 text-left">Month</th>
              <th className="px-3 py-2 text-right">Actual</th>
              <th className="px-3 py-2 text-right">AOP Plan</th>
              <th className="px-3 py-2 text-right">Variance</th>
              <th className="px-3 py-2 text-right">₹ / bed</th>
              <th className="px-3 py-2 text-right">₹ / bed-night</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.key} className="hover:bg-zinc-50" data-testid={`rent-row-${r.key}`}>
                <td className="px-3 py-2 font-medium">{r.month_label}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtINR(r.rent_actual)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{fmtINR(r.rent_plan)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.variance_pct == null ? "text-zinc-400" : r.variance_pct > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  {r.variance_pct == null ? "—" : `${r.variance_pct > 0 ? "+" : ""}${r.variance_pct}%`}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-600">{fmtINR(r.per_bed)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-600">{fmtINR(r.per_bed_night)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
